import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Loader2, CheckCircle2, XCircle, Eye, Banknote, Ban, Clock, ArrowRight, Package,
} from 'lucide-react';
import { reimbursementAPI } from '@/lib/api';
import ReceiptThumb from './ReceiptThumb';
import { CATEGORY_LABEL, STATUS_META, money } from './constants';

const Row = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-1 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-800 text-right">{children}</span>
  </div>
);

export default function ReimbursementDetailDialog({ open, onOpenChange, request, admin = false, onChanged }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [paying, setPaying] = useState(false);
  const [method, setMethod] = useState('');
  const [reference, setReference] = useState('');

  const reqId = request?.request_id;

  const refetch = async () => {
    if (!reqId) return;
    try {
      const r = await reimbursementAPI.get(reqId);
      setDetail(r.data);
    } catch { /* keep summary */ }
  };

  useEffect(() => {
    if (open && reqId) { setDetail(null); refetch(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reqId]);

  if (!request) return null;
  const r = detail || request;
  const meta = STATUS_META[r.status] || {};
  const pl = r.payment_link;

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      toast.success(okMsg);
      onChanged?.(res.data);
      setDetail(res.data);
      await refetch();
      setRejecting(false); setPaying(false); setReason(''); setMethod(''); setReference('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const canCancel = !admin && ['submitted', 'under_review'].includes(r.status);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reimbursement-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">
            {r.title}
            <Badge className={`text-[10px] border ${meta.cls || ''}`} data-testid="reimbursement-detail-status">{meta.label || r.status}</Badge>
          </DialogTitle>
          <DialogDescription>{CATEGORY_LABEL[r.category] || r.category} · submitted by {r.requester_name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <Row label="Amount"><span className="text-emerald-700">{money(r.amount)}</span></Row>
            {r.expense_date && <Row label="Expense date">{r.expense_date}</Row>}
            {r.description && <Row label="Details">{r.description}</Row>}
            {admin && r.requester_npn && <Row label="Agent NPN">{r.requester_npn}</Row>}
            {r.reject_reason && <Row label="Reason"><span className="text-rose-600">{r.reject_reason}</span></Row>}
            {r.payment_method && <Row label="Payment method">{r.payment_method}</Row>}
            {r.payment_reference && <Row label="Payment ref">{r.payment_reference}</Row>}
          </div>

          {/* Where the payment sits in the Accounting pipeline (after approval) */}
          {pl && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2.5" data-testid="reimbursement-payment-link">
              {pl.stage === 'batch' ? (
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-sm text-indigo-900 flex items-center gap-1.5">
                    <Package size={14} className="text-indigo-600" /> In Payment Batch
                    <span className="font-semibold">{pl.batch_number}</span>
                    {pl.batch_status && <Badge className="text-[10px] border bg-white text-indigo-700 border-indigo-200 capitalize">{pl.batch_status}</Badge>}
                  </span>
                  {admin && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate(`/accounting/payment-batches/${pl.batch_id}`)} data-testid="reimbursement-open-batch">
                      Open batch <ArrowRight size={12} className="ml-1" />
                    </Button>
                  )}
                </div>
              ) : pl.stage === 'statement' ? (
                <span className="text-sm text-indigo-900 flex items-center gap-1.5">
                  <ArrowRight size={13} /> Statement <span className="font-semibold">{pl.statement_number}</span> generated — not yet in a Payment Batch.
                </span>
              ) : r.status === 'paid' ? (
                <span className="text-sm text-emerald-800 flex items-center gap-1.5">
                  <Banknote size={13} className="text-emerald-600" /> Paid and recorded in Accounting.
                </span>
              ) : admin ? (
                <span className="text-sm text-indigo-900 flex items-center gap-1.5">
                  <ArrowRight size={13} /> Added to Payments — generate a statement, then include it in a Payment Batch.
                </span>
              ) : (
                <span className="text-sm text-indigo-900 flex items-center gap-1.5">
                  <ArrowRight size={13} /> Approved — queued for payment.
                </span>
              )}
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-slate-500 mb-1.5">Receipts ({(r.receipts || []).length})</p>
            <div className="flex flex-wrap gap-2" data-testid="reimbursement-detail-receipts">
              {(r.receipts || []).map((rec) => (
                <ReceiptThumb key={rec.storage_key} requestId={r.request_id} receipt={rec} />
              ))}
            </div>
          </div>

          {(r.history || []).length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1.5">Activity</p>
              <ol className="space-y-1.5">
                {r.history.map((h, i) => {
                  const hm = STATUS_META[h.status] || {};
                  return (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                      <Clock size={12} className="mt-0.5 text-slate-300 shrink-0" />
                      <span>
                        <span className="font-medium text-slate-700">{hm.label || h.status}</span>
                        {h.note ? ` — ${h.note}` : ''} · {h.by_name}
                        <span className="text-slate-400"> · {h.at ? new Date(h.at).toLocaleString() : ''}</span>
                      </span>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="pt-2 border-t border-slate-100 space-y-2">
          {admin && r.status === 'submitted' && (
            <Button className="w-full" variant="outline" disabled={busy} onClick={() => act(() => reimbursementAPI.review(r.request_id), 'Moved to review')} data-testid="reimbursement-review-btn">
              <Eye size={14} className="mr-1.5" /> Start review
            </Button>
          )}

          {admin && ['submitted', 'under_review'].includes(r.status) && !rejecting && (
            <div className="flex gap-2">
              <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700" disabled={busy} onClick={() => act(() => reimbursementAPI.approve(r.request_id), 'Approved & sent to payments')} data-testid="reimbursement-approve-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 size={14} className="mr-1.5" />} Approve
              </Button>
              <Button className="flex-1" variant="outline" disabled={busy} onClick={() => setRejecting(true)} data-testid="reimbursement-reject-open">
                <XCircle size={14} className="mr-1.5 text-rose-600" /> Reject
              </Button>
            </div>
          )}

          {admin && rejecting && (
            <div className="space-y-2">
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Reason for rejection (shared with the agent)" data-testid="reimbursement-reject-reason" />
              <div className="flex gap-2">
                <Button className="flex-1" variant="outline" onClick={() => setRejecting(false)}>Back</Button>
                <Button className="flex-1 bg-rose-600 hover:bg-rose-700" disabled={busy || !reason.trim()} onClick={() => act(() => reimbursementAPI.reject(r.request_id, reason.trim()), 'Rejected')} data-testid="reimbursement-reject-confirm">Confirm reject</Button>
              </div>
            </div>
          )}

          {admin && r.status === 'approved' && !paying && (
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => setPaying(true)} data-testid="reimbursement-pay-open">
              <Banknote size={14} className="mr-1.5" /> Mark as paid
            </Button>
          )}

          {admin && paying && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Input value={method} onChange={(e) => setMethod(e.target.value)} placeholder="Method (e.g. ACH)" data-testid="reimbursement-pay-method" />
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Reference #" data-testid="reimbursement-pay-reference" />
              </div>
              <div className="flex gap-2">
                <Button className="flex-1" variant="outline" onClick={() => setPaying(false)}>Back</Button>
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act(() => reimbursementAPI.markPaid(r.request_id, { method: method.trim(), reference: reference.trim() }), 'Marked as paid')} data-testid="reimbursement-pay-confirm">Confirm paid</Button>
              </div>
            </div>
          )}

          {canCancel && (
            <Button className="w-full" variant="outline" disabled={busy} onClick={() => act(() => reimbursementAPI.cancel(r.request_id), 'Request cancelled')} data-testid="reimbursement-cancel-btn">
              <Ban size={14} className="mr-1.5" /> Cancel request
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
